
import React, { useState, useEffect, useMemo } from 'react';
import { AppState, UserProfile, Community, CommunityTheme, JoinSetting, Meeting, MatchResult, CommunityMember, SiteConfig } from './types';
import { CITIES, ACTIVITY_SPHERES } from './constants';
import { Navbar } from './components/Navbar';
import { ProfileForm } from './components/ProfileForm';
import { Landing } from './components/Landing';
import { AdminPanel } from './components/AdminPanel';
import { AuthModal } from './components/AuthModal';
import { CommunityForm, CommunityFormData } from './components/CommunityForm';
import { MeetingModal } from './components/MeetingModal';
import { UserCardModal } from './components/UserCardModal';
import { supabase } from './services/supabaseClient';
import { sendTelegramMessage, TG_NOTIFICATIONS } from './services/telegramService';
import { findMatches } from './services/geminiService';

const App: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [meetingTarget, setMeetingTarget] = useState<UserProfile | null>(null);
  const [meetingToEdit, setMeetingToEdit] = useState<Meeting | null>(null);
  const [selectedUserForView, setSelectedUserForView] = useState<UserProfile | null>(null);
  const [toasts, setToasts] = useState<{id: string, message: string, type: 'success' | 'error'}[]>([]);
  
  const [activeTab, setActiveTab] = useState<'users' | 'communities' | 'meetings'>('users');
  const [userSubTab, setUserSubTab] = useState<'all' | 'search' | 'favorites'>('all');
  const [commSubTab, setCommSubTab] = useState<'all' | 'my' | 'favorites'>('all');
  const [meetingSubTab, setMeetingSubTab] = useState<'incoming' | 'outgoing' | 'scheduled' | 'archive'>('incoming');
  
  const [filters, setFilters] = useState({ city: 'Все города', activity: 'Все сферы' });
  const [commFilters, setCommFilters] = useState({ city: 'Все города', theme: 'any' });
  const [activeCommFilters, setActiveCommFilters] = useState({ city: 'Все города', theme: 'any' });
  
  const [aiSearchQuery, setAiSearchQuery] = useState('');
  const [aiMatches, setAiMatches] = useState<MatchResult[]>([]);
  const [isAiMatching, setIsAiMatching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [memberships, setMemberships] = useState<CommunityMember[]>([]);

  const [state, setState] = useState<AppState>({
    currentUser: null,
    communities: [],
    users: [],
    meetings: [],
    config: null
  });

  const [activeView, setActiveView] = useState<'dashboard' | 'profile' | 'create_community' | 'admin' | 'landing'>('landing');

  const addToast = (message: string, type: 'success' | 'error' = 'success') => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, {id, message, type}]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  };

  const fetchData = async (userToKeep?: UserProfile | null) => {
    try {
      const [commsRes, profilesRes, meetingsRes, membersRes, configRes] = await Promise.all([
        supabase.from('communities').select('*'),
        supabase.from('profiles').select('*'),
        supabase.from('meetings').select('*').order('created_at', { ascending: false }),
        supabase.from('community_members').select('*'),
        supabase.from('site_config').select('*').eq('id', 'main').maybeSingle()
      ]);

      const dbProfiles: UserProfile[] = (profilesRes.data || []).map(p => ({
        id: p.id, name: p.name || '', photo: p.photo || '', tgHandle: p.tg_handle || '',
        tgChatId: p.tg_chat_id, password: p.password || '', city: p.city || CITIES[0],
        activitySphere: p.activity_sphere || ACTIVITY_SPHERES[0], interests: p.interests || [],
        goal: p.goal || 'both', format: p.format || 'both', aboutMe: p.about_me || '',
        canHelpWith: p.can_help_with || '', lookingFor: p.looking_for || '',
        role: p.role || 'user', visibility: p.visibility || 'community_only', bot_connected: !!p.tg_chat_id
      }));

      const dbComms: Community[] = (commsRes.data || []).map(c => ({
        id: c.id, name: c.name || '', description: c.description || '', targetAudience: c.target_audience || '',
        logo: c.logo || '', tgLink: c.tg_link || '', memberCount: c.member_count || 0,
        joinSetting: (c.join_setting as JoinSetting) || 'auto', status: c.status || 'pending',
        creatorId: c.creator_id || '', city: c.city || '', theme: (c.theme as CommunityTheme) || 'any'
      }));

      const dbMeetings: Meeting[] = (meetingsRes.data || []).map(m => ({
        id: m.id, inviterId: m.inviter_id, inviteeId: m.invitee_id, status: m.status,
        meeting_date: m.meeting_date, meeting_time: m.meeting_time, location: m.location,
        message: m.message, format: m.format || 'offline', decline_reason: m.decline_reason,
        last_changed_by: m.last_changed_by, created_at: m.created_at
      }));

      const dbMembers = membersRes.data?.map(m => ({ userId: m.user_id, communityId: m.community_id, status: m.status, request_message: m.request_message })) || [];
      setMemberships(dbMembers);

      let currentUser = userToKeep || state.currentUser || null;
      if (currentUser) {
        const freshUser = dbProfiles.find(u => u.id === currentUser?.id);
        if (freshUser) currentUser = { ...freshUser, lastLogin: Date.now() };
      }

      setState({ communities: dbComms, users: dbProfiles, meetings: dbMeetings, currentUser, config: configRes.data as SiteConfig || null });
      if (currentUser) {
        setIsLoggedIn(true);
        localStorage.setItem('rc_vn_user', JSON.stringify(currentUser));
        if (activeView === 'landing') setActiveView('dashboard');
      }
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => {
    const savedUserStr = localStorage.getItem('rc_vn_user');
    const savedFavs = localStorage.getItem('rc_vn_favs');
    if (savedFavs) setFavorites(JSON.parse(savedFavs));
    
    if (savedUserStr) {
      const savedUser = JSON.parse(savedUserStr) as UserProfile;
      const thirtyDays = 30 * 24 * 60 * 60 * 1000;
      if (savedUser.lastLogin && (Date.now() - savedUser.lastLogin < thirtyDays)) {
        fetchData(savedUser);
      } else {
        localStorage.removeItem('rc_vn_user');
        fetchData();
      }
    } else {
      fetchData();
    }
  }, []);

  const handleJoinLeaveCommunity = async (communityId: string, action: 'join' | 'leave' | 'request', message?: string) => {
    if (!state.currentUser) return;
    try {
      if (action === 'leave') {
        await supabase.from('community_members').delete().eq('user_id', state.currentUser.id).eq('community_id', communityId);
        addToast('Вы покинули клуб');
      } else {
        const status = action === 'request' ? 'pending' : 'active';
        await supabase.from('community_members').insert([{ 
          user_id: state.currentUser.id, 
          community_id: communityId, 
          status, 
          request_message: message 
        }]);
        addToast(status === 'active' ? 'Вы вступили в клуб!' : 'Заявка отправлена!');
      }
      fetchData(state.currentUser);
    } catch (err: any) { addToast(err.message, 'error'); }
  };

  const handleRunAiMatch = async () => {
    if (!aiSearchQuery) return;
    setIsAiMatching(true);
    setHasSearched(true);
    try {
      const potential = state.users.filter(u => u.id !== state.currentUser?.id).slice(0, 50);
      const matches = await findMatches({ ...state.currentUser!, lookingFor: aiSearchQuery }, potential);
      setAiMatches(matches);
    } catch (e) {
      addToast('Ошибка AI подбора', 'error');
    } finally {
      setIsAiMatching(false);
    }
  };

  // Add missing toggleFavorite function to handle user and community favorites
  const toggleFavorite = (id: string) => {
    setFavorites(prev => {
      const newFavs = prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id];
      localStorage.setItem('rc_vn_favs', JSON.stringify(newFavs));
      return newFavs;
    });
  };

  // Add missing handleSaveNewCommunity function to handle the creation of new communities
  const handleSaveNewCommunity = async (formData: CommunityFormData) => {
    if (!state.currentUser) return;
    try {
      const { error } = await supabase.from('communities').insert([{
        name: formData.name,
        description: formData.description,
        target_audience: formData.targetAudience,
        logo: formData.logo,
        tg_link: formData.tgLink,
        join_setting: formData.joinSetting,
        city: formData.city,
        theme: formData.theme,
        creator_id: state.currentUser.id,
        status: 'active',
        member_count: 1
      }]);

      if (error) throw error;

      addToast('Клуб успешно создан!');
      setActiveView('dashboard');
      fetchData(state.currentUser);
    } catch (err: any) {
      addToast(err.message, 'error');
    }
  };

  const filteredUsers = useMemo(() => {
    if (userSubTab === 'favorites') return state.users.filter(u => favorites.includes(u.id));
    if (userSubTab === 'search' && !hasSearched && filters.city === 'Все города' && filters.activity === 'Все сферы') return [];

    let list = state.users.filter(u => u.id !== state.currentUser?.id);
    if (filters.city !== 'Все города') list = list.filter(u => u.city === filters.city);
    if (filters.activity !== 'Все сферы') list = list.filter(u => u.activitySphere === filters.activity);
    
    if (aiMatches.length > 0) {
      const matchIds = aiMatches.map(m => m.userId);
      list = [...list].sort((a, b) => {
        const aMatch = matchIds.indexOf(a.id);
        const bMatch = matchIds.indexOf(b.id);
        if (aMatch !== -1 && bMatch === -1) return -1;
        if (bMatch !== -1 && aMatch === -1) return 1;
        return 0;
      });
    }
    return list;
  }, [state.users, filters, userSubTab, favorites, state.currentUser, aiMatches, hasSearched]);

  const sortedCommunities = useMemo(() => {
    let list = [...state.communities];
    if (commSubTab === 'my') {
      list = list.filter(c => c.creatorId === state.currentUser?.id);
    } else if (commSubTab === 'favorites') {
      list = list.filter(c => favorites.includes(c.id));
    } else {
      list = list.filter(c => c.status === 'active');
      // Сортировка: сначала те, в которых состою
      list.sort((a, b) => {
        const aIn = memberships.some(m => m.communityId === a.id && m.userId === state.currentUser?.id && m.status === 'active') ? 0 : 1;
        const bIn = memberships.some(m => m.communityId === b.id && m.userId === state.currentUser?.id && m.status === 'active') ? 0 : 1;
        return aIn - bIn;
      });
    }

    if (activeCommFilters.city !== 'Все города') list = list.filter(c => c.city === activeCommFilters.city);
    if (activeCommFilters.theme !== 'any') list = list.filter(c => c.theme === activeCommFilters.theme);
    
    return list;
  }, [state.communities, commSubTab, favorites, state.currentUser, memberships, activeCommFilters]);

  const filteredMeetings = useMemo(() => {
    return state.meetings.filter(m => {
      const isMeInviter = m.inviterId === state.currentUser?.id;
      const isMeInvitee = m.inviteeId === state.currentUser?.id;
      if (meetingSubTab === 'incoming') return isMeInvitee && m.status === 'pending';
      if (meetingSubTab === 'outgoing') return isMeInviter && m.status === 'pending';
      if (meetingSubTab === 'scheduled') return (isMeInvitee || isMeInviter) && m.status === 'scheduled';
      if (meetingSubTab === 'archive') return (isMeInvitee || isMeInviter) && (m.status === 'completed' || m.status === 'declined');
      return false;
    });
  }, [state.meetings, meetingSubTab, state.currentUser]);

  const handleActionMeeting = async (meetingId: string, action: 'scheduled' | 'declined', reason?: string) => {
    try {
      const meeting = state.meetings.find(m => m.id === meetingId);
      const partnerId = meeting?.inviterId === state.currentUser?.id ? meeting?.inviteeId : meeting?.inviterId;
      const partner = state.users.find(u => u.id === partnerId);
      
      const { error } = await supabase.from('meetings').update({ status: action, decline_reason: reason || null, last_changed_by: state.currentUser?.id }).eq('id', meetingId);
      if (error) throw error;
      
      if (partner?.tgChatId) {
        if (action === 'scheduled') await sendTelegramMessage(partner.tgChatId, TG_NOTIFICATIONS.inviteAccepted(state.currentUser?.name!, meeting?.meeting_date!, meeting?.meeting_time!, meeting?.location!));
        else await sendTelegramMessage(partner.tgChatId, TG_NOTIFICATIONS.inviteDeclined(state.currentUser?.name!, reason));
      }
      addToast(action === 'scheduled' ? 'Встреча подтверждена!' : 'Встреча отклонена');
      fetchData(state.currentUser);
    } catch (err: any) { addToast(err.message, 'error'); }
  };

  const handleSendInvite = async (data: { date: string, time: string, location: string, message: string, format: string }) => {
    if (!state.currentUser) return;
    try {
      const isEdit = !!meetingToEdit;
      const targetUser = meetingToEdit ? state.users.find(u => u.id === (meetingToEdit.inviterId === state.currentUser?.id ? meetingToEdit.inviteeId : meetingToEdit.inviterId)) : meetingTarget;
      if (!targetUser) throw new Error("Цель не найдена");
      
      const payload = { 
        inviter_id: meetingToEdit ? meetingToEdit.inviterId : state.currentUser.id, 
        invitee_id: meetingToEdit ? meetingToEdit.inviteeId : targetUser.id, 
        status: 'pending', 
        meeting_date: data.date, 
        meeting_time: data.time, 
        location: data.location, 
        message: data.message, 
        format: data.format, 
        last_changed_by: state.currentUser.id 
      };
      
      const { error } = isEdit ? await supabase.from('meetings').update(payload).eq('id', meetingToEdit!.id) : await supabase.from('meetings').insert([payload]);
      if (error) throw error;
      
      if (targetUser.tgChatId) await sendTelegramMessage(targetUser.tgChatId, TG_NOTIFICATIONS.inviteReceived(state.currentUser.name));
      addToast(isEdit ? 'Запрос обновлен!' : 'Приглашение отправлено!');
      setMeetingTarget(null); setMeetingToEdit(null); fetchData(state.currentUser);
    } catch (err: any) { addToast(err.message, 'error'); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fcfaf8] pb-10" style={{ fontFamily: state.config?.font_family || 'Inter' }}>
      <Navbar onNavigate={v => isLoggedIn ? setActiveView(v as any) : setShowAuthModal(true)} activeView={activeView} isLoggedIn={isLoggedIn} userRole={state.currentUser?.role} onLogin={() => setShowAuthModal(true)} onLogout={() => { localStorage.removeItem('rc_vn_user'); setIsLoggedIn(false); setActiveView('landing'); }} pendingInvites={state.meetings.filter(m => m.inviteeId === state.currentUser?.id && m.status === 'pending')} users={state.users} onActionMeeting={handleActionMeeting} botConnected={state.currentUser?.bot_connected} />

      <main className={`flex-grow ${activeView === 'landing' ? 'w-full' : 'container mx-auto px-4 py-6 md:py-12 max-w-6xl'}`}>
        {activeView === 'landing' && <Landing onJoin={() => setShowAuthModal(true)} config={state.config} />}
        
        {activeView === 'dashboard' && (
          <div className="space-y-8 animate-fadeIn">
            {/* TABS */}
            <div className="flex flex-col gap-6 border-b border-gray-100 pb-4">
              <div className="flex flex-wrap justify-between items-center gap-4">
                <div className="flex gap-6 md:gap-10 overflow-x-auto no-scrollbar py-2">
                  {['users', 'communities', 'meetings'].map((t: any) => (
                    <button key={t} onClick={() => setActiveTab(t)} className={`pb-4 text-sm md:text-xl font-black uppercase tracking-widest transition-all border-b-4 ${activeTab === t ? 'text-black border-black' : 'text-gray-300 border-transparent'}`}>
                      {t === 'users' ? 'Участники' : t === 'communities' ? 'Клубы' : 'Встречи'}
                    </button>
                  ))}
                </div>
                {activeTab === 'communities' && <button onClick={() => setActiveView('create_community')} className="px-8 py-3.5 bg-black text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-red-600 transition-colors shadow-xl">➕ Создать клуб</button>}
              </div>

              <div className="flex gap-2 overflow-x-auto no-scrollbar">
                {activeTab === 'users' && ['all', 'search', 'favorites'].map(s => <button key={s} onClick={() => { setUserSubTab(s as any); if(s!=='search') {setAiMatches([]); setHasSearched(false); } }} className={`px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest whitespace-nowrap ${userSubTab === s ? 'bg-black text-white' : 'bg-gray-100 text-gray-400'}`}>{s === 'all' ? 'Все' : s === 'search' ? 'Поиск & AI' : 'Избранные'}</button>)}
                {activeTab === 'communities' && ['all', 'my', 'favorites'].map(s => <button key={s} onClick={() => setCommSubTab(s as any)} className={`px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest whitespace-nowrap ${commSubTab === s ? 'bg-black text-white' : 'bg-gray-100 text-gray-400'}`}>{s === 'all' ? 'Все' : s === 'my' ? 'Мои' : 'Избранные'}</button>)}
                {activeTab === 'meetings' && ['incoming', 'outgoing', 'scheduled', 'archive'].map(s => <button key={s} onClick={() => setMeetingSubTab(s as any)} className={`px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest whitespace-nowrap ${meetingSubTab === s ? 'bg-black text-white' : 'bg-gray-100 text-gray-400'}`}>{s === 'incoming' ? 'Заявки мне' : s === 'outgoing' ? 'Мои запросы' : s === 'scheduled' ? 'Предстоящие' : 'Архив'}</button>)}
              </div>
            </div>

            {/* FILTERS FOR USERS */}
            {activeTab === 'users' && userSubTab === 'search' && (
              <div className="bg-white p-10 rounded-[3rem] shadow-2xl border border-gray-100 space-y-8 animate-slideDown">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-1"><label className="text-[10px] font-black uppercase text-gray-400 mb-2 block">Город</label><select className="w-full bg-gray-50 p-4 rounded-2xl text-[11px] font-bold outline-none border border-gray-100" value={filters.city} onChange={e => { setFilters({...filters, city: e.target.value}); setHasSearched(true); }}>{['Все города', ...CITIES].map(c => <option key={c}>{c}</option>)}</select></div>
                  <div className="flex-1"><label className="text-[10px] font-black uppercase text-gray-400 mb-2 block">Сфера</label><select className="w-full bg-gray-50 p-4 rounded-2xl text-[11px] font-bold outline-none border border-gray-100" value={filters.activity} onChange={e => { setFilters({...filters, activity: e.target.value}); setHasSearched(true); }}>{['Все сферы', ...ACTIVITY_SPHERES].map(s => <option key={s}>{s}</option>)}</select></div>
                </div>
                <div className="relative">
                  <input type="text" className="w-full bg-gray-50 p-6 pr-40 rounded-[2rem] text-sm font-bold border border-gray-100 outline-none focus:ring-4 focus:ring-red-500/10 transition-all" placeholder="Кого вы ищете? Напр: Инвестор в недвижимость Нячанга" value={aiSearchQuery} onChange={e => setAiSearchQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleRunAiMatch()} />
                  <button onClick={handleRunAiMatch} disabled={isAiMatching || !aiSearchQuery} className="absolute right-3 top-3 bottom-3 px-8 bg-black text-white rounded-[1.5rem] text-[10px] font-black uppercase tracking-widest hover:bg-red-600 transition-all">
                    {isAiMatching ? '◌◌◌' : '✨ AI ПОДБОР'}
                  </button>
                </div>
              </div>
            )}

            {/* FILTERS FOR COMMUNITIES */}
            {activeTab === 'communities' && commSubTab === 'all' && (
              <div className="flex flex-wrap gap-4 items-center bg-white p-6 rounded-3xl border border-gray-50 shadow-sm">
                <div className="flex-1 min-w-[150px]">
                   <label className="text-[9px] font-black uppercase text-gray-400 block mb-1">Город</label>
                   <select className="w-full bg-gray-50 px-4 py-2 rounded-xl text-[10px] font-bold" value={commFilters.city} onChange={e => setCommFilters({...commFilters, city: e.target.value})}>{['Все города', ...CITIES].map(c => <option key={c}>{c}</option>)}</select>
                </div>
                <div className="flex-1 min-w-[150px]">
                   <label className="text-[9px] font-black uppercase text-gray-400 block mb-1">Тематика</label>
                   <select className="w-full bg-gray-50 px-4 py-2 rounded-xl text-[10px] font-bold" value={commFilters.theme} onChange={e => setCommFilters({...commFilters, theme: e.target.value as any})}>
                     <option value="any">Все тематики</option><option value="business">Бизнес</option><option value="lifestyle">Лайфстайл</option>
                   </select>
                </div>
                <div className="flex gap-2 pt-4 md:pt-0">
                   <button onClick={() => setActiveCommFilters(commFilters)} className="px-6 py-3 bg-black text-white rounded-xl text-[9px] font-black uppercase tracking-widest">Применить</button>
                   <button onClick={() => { setCommFilters({city: 'Все города', theme: 'any'}); setActiveCommFilters({city: 'Все города', theme: 'any'}); }} className="px-6 py-3 bg-gray-100 text-gray-400 rounded-xl text-[9px] font-black uppercase tracking-widest">Очистить</button>
                </div>
              </div>
            )}

            {/* CONTENT GRID */}
            <div className="grid grid-cols-1 gap-6">
              {activeTab === 'users' && filteredUsers.map(u => {
                const aiMatch = aiMatches.find(m => m.userId === u.id);
                return (
                  <div key={u.id} className={`bg-white p-8 rounded-[3rem] border shadow-sm flex flex-col md:flex-row items-center gap-8 group hover:shadow-2xl transition-all ${aiMatch ? 'border-red-500 ring-2 ring-red-500/5' : 'border-gray-100'}`}>
                    <img src={u.photo} className="w-32 h-32 rounded-[2.5rem] object-cover shadow-xl group-hover:scale-105 transition-transform" />
                    <div className="flex-1 text-center md:text-left min-w-0">
                      <h3 className="text-3xl font-black uppercase tracking-tighter mb-1">{u.name} <span className="text-[10px] text-gray-400 font-bold ml-2 tracking-widest">{u.city}</span></h3>
                      <p className="text-[10px] font-black text-red-600 uppercase tracking-widest mb-4">{u.activitySphere}</p>
                      <p className="text-sm text-gray-500 italic line-clamp-2 leading-relaxed">"{u.aboutMe}"</p>
                      {aiMatch && <div className="mt-4 p-4 bg-red-50 rounded-2xl border border-red-100"><p className="text-[9px] font-black text-red-600 uppercase mb-1">AI Совет:</p><p className="text-xs text-gray-600 italic">{aiMatch.reasoning}</p></div>}
                    </div>
                    <div className="flex flex-row md:flex-col gap-3 w-full md:w-auto">
                      <button onClick={() => setSelectedUserForView(u)} className="flex-1 px-8 py-4 bg-gray-100 text-[9px] font-black uppercase rounded-2xl">Анкета</button>
                      <button onClick={() => setMeetingTarget(u)} className="flex-[1.5] px-8 py-4 bg-black text-white text-[9px] font-black uppercase rounded-2xl hover:bg-red-600 transition-all flex items-center justify-center gap-2">☕️ Кофе</button>
                      <button onClick={() => toggleFavorite(u.id)} className={`px-5 py-4 border-2 rounded-2xl ${favorites.includes(u.id) ? 'bg-red-50 border-red-200 text-red-600' : 'bg-white border-gray-100 text-gray-300'}`}>{favorites.includes(u.id) ? '❤️' : '🤍'}</button>
                    </div>
                  </div>
                );
              })}
              {activeTab === 'users' && userSubTab === 'search' && filteredUsers.length === 0 && (
                <div className="py-20 text-center opacity-30"><p className="text-6xl mb-6">🔎</p><p className="font-black uppercase tracking-widest text-[10px]">Используйте поиск или фильтры выше</p></div>
              )}
            </div>

            {activeTab === 'communities' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {sortedCommunities.map(c => {
                  const myMembership = memberships.find(m => m.communityId === c.id && m.userId === state.currentUser?.id);
                  const isCreator = c.creatorId === state.currentUser?.id;
                  const pendingRequestsCount = memberships.filter(m => m.communityId === c.id && m.status === 'pending').length;
                  
                  return (
                    <div key={c.id} className="bg-white p-10 rounded-[3.5rem] border border-gray-100 shadow-sm hover:shadow-2xl transition-all group">
                      <div className="flex items-center gap-6 mb-8">
                        <img src={c.logo} className="w-24 h-24 rounded-[2rem] object-cover shadow-xl group-hover:scale-110 transition-transform" />
                        <div>
                          <h3 className="font-black text-2xl uppercase tracking-tighter mb-1">{c.name}</h3>
                          <p className="text-[10px] text-red-600 uppercase font-black tracking-widest">{c.city} • {c.memberCount} Уч. • {c.joinSetting === 'auto' ? 'Открытый' : 'Закрытый'}</p>
                        </div>
                      </div>
                      <p className="text-[10px] font-black uppercase text-gray-400 mb-2">Целевая аудитория:</p>
                      <p className="text-sm font-bold text-gray-800 mb-8">{c.targetAudience}</p>
                      
                      <div className="flex gap-4">
                        {isCreator ? (
                          <div className="flex-1 flex gap-2">
                             <button onClick={() => {}} className="flex-1 py-4 bg-black text-white text-[10px] font-black uppercase rounded-2xl">Изменить</button>
                             {c.joinSetting === 'manual' && pendingRequestsCount > 0 && (
                               <button className="bg-red-600 text-white px-6 py-4 rounded-2xl text-[10px] font-black uppercase">Заявки ({pendingRequestsCount})</button>
                             )}
                          </div>
                        ) : myMembership?.status === 'active' ? (
                          <div className="flex-1 flex gap-2">
                            <a href={c.tgLink} target="_blank" className="flex-1 py-4 bg-indigo-600 text-white text-[10px] font-black uppercase rounded-2xl text-center">В Группу</a>
                            <button onClick={() => handleJoinLeaveCommunity(c.id, 'leave')} className="px-6 py-4 bg-red-50 text-red-600 text-[9px] font-black uppercase rounded-2xl">Выйти</button>
                          </div>
                        ) : myMembership?.status === 'pending' ? (
                          <button disabled className="flex-1 py-4 bg-gray-100 text-gray-400 text-[10px] font-black uppercase rounded-2xl">На рассмотрении</button>
                        ) : (
                          <button 
                            onClick={() => {
                              if (c.joinSetting === 'manual') {
                                const msg = prompt('Почему вас стоит принять?');
                                if (msg) handleJoinLeaveCommunity(c.id, 'request', msg);
                              } else {
                                handleJoinLeaveCommunity(c.id, 'join');
                              }
                            }} 
                            className="flex-1 py-4 bg-black text-white text-[10px] font-black uppercase rounded-2xl"
                          >
                            {c.joinSetting === 'manual' ? 'Подать заявку' : 'Вступить'}
                          </button>
                        )}
                        <button onClick={() => toggleFavorite(c.id)} className={`px-6 py-4 border-2 rounded-2xl ${favorites.includes(c.id) ? 'bg-red-50 border-red-100 text-red-600' : 'border-gray-100 text-gray-300'}`}>{favorites.includes(c.id) ? '❤️' : '🤍'}</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {activeTab === 'meetings' && (
              <div className="grid grid-cols-1 gap-4">
                {filteredMeetings.map(m => {
                  const partnerId = m.inviterId === state.currentUser?.id ? m.inviteeId : m.inviterId;
                  const partner = state.users.find(u => u.id === partnerId);
                  const isIncoming = m.inviteeId === state.currentUser?.id && m.status === 'pending';
                  const isOutgoingOrScheduled = (m.inviterId === state.currentUser?.id && m.status === 'pending') || m.status === 'scheduled';

                  return (
                    <div key={m.id} className="bg-white p-8 rounded-[3rem] border border-gray-100 flex flex-col md:flex-row items-center gap-6 shadow-sm">
                      <img src={partner?.photo} className="w-20 h-20 rounded-2xl object-cover" />
                      <div className="flex-1">
                        <h4 className="font-black uppercase tracking-tighter">{partner?.name}</h4>
                        <p className="text-[10px] font-bold text-gray-400 mb-2 uppercase">{m.meeting_date} • {m.meeting_time} • {m.location}</p>
                        <p className="text-xs text-gray-600 italic line-clamp-1 leading-relaxed">"{m.message}"</p>
                      </div>
                      <div className="flex gap-2">
                        {isIncoming && (
                          <div className="flex gap-2">
                            <button onClick={() => setMeetingToEdit(m)} className="px-6 py-3 bg-black text-white rounded-xl text-[10px] font-black uppercase">Принять</button>
                            <button onClick={() => handleActionMeeting(m.id, 'declined')} className="px-6 py-3 bg-gray-50 text-gray-400 rounded-xl text-[10px] font-black uppercase">Отклонить</button>
                          </div>
                        )}
                        {isOutgoingOrScheduled && (
                          <button onClick={() => setMeetingToEdit(m)} className="px-6 py-3 bg-indigo-50 text-indigo-600 rounded-xl text-[10px] font-black uppercase">Изменить</button>
                        )}
                        <button onClick={() => partner && setSelectedUserForView(partner)} className="px-6 py-3 bg-gray-100 rounded-xl text-[10px] font-black uppercase">Анкета</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeView === 'profile' && <ProfileForm user={state.currentUser} onSave={fetchData} />}
        {activeView === 'admin' && <AdminPanel users={state.users} communities={state.communities} meetings={state.meetings} config={state.config} onUpdateConfig={() => {}} onLogout={() => { localStorage.removeItem('rc_vn_user'); setIsLoggedIn(false); setActiveView('landing'); }} onViewUser={setSelectedUserForView} onRefresh={fetchData} addToast={addToast} />}
        {activeView === 'create_community' && <CommunityForm onSave={handleSaveNewCommunity} onCancel={() => setActiveView('dashboard')} />}
      </main>

      {/* MODALS */}
      {selectedUserForView && <div className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setSelectedUserForView(null)}><UserCardModal user={selectedUserForView} onClose={() => setSelectedUserForView(null)} onInvite={setMeetingTarget} /></div>}
      
      {meetingTarget && (
        <div className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setMeetingTarget(null)}>
          <MeetingModal targetUser={meetingTarget} currentUser={state.currentUser} onClose={() => setMeetingTarget(null)} onSubmit={handleSendInvite} />
        </div>
      )}

      {meetingToEdit && (
        <div className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setMeetingToEdit(null)}>
          <MeetingModal 
            targetUser={state.users.find(u => u.id === (meetingToEdit.inviterId === state.currentUser?.id ? meetingToEdit.inviteeId : meetingToEdit.inviterId))!} 
            currentUser={state.currentUser} 
            initialData={meetingToEdit}
            onClose={() => setMeetingToEdit(null)} 
            onSubmit={handleSendInvite} 
          />
        </div>
      )}

      {showAuthModal && <div className="fixed inset-0 z-[150] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setShowAuthModal(false)}><AuthModal onClose={() => setShowAuthModal(false)} onSuccess={u => { fetchData(u); setShowAuthModal(false); }} onNewUser={() => { setActiveView('profile'); setShowAuthModal(false); }} /></div>}
      
      <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-[300] flex flex-col gap-3 w-full max-w-xs md:max-w-md px-4">
        {toasts.map(t => <div key={t.id} className={`px-6 py-4 rounded-2xl shadow-2xl font-black text-[10px] uppercase tracking-[0.2em] animate-fadeIn text-center ${t.type === 'success' ? 'bg-black text-white' : 'bg-red-600 text-white'}`}>{t.message}</div>)}
      </div>
    </div>
  );
};

export default App;
